import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { richteTresorEin } from 'core/features/sicherheit/sicherheitSlice';
import { baueKeyfileInhalt } from 'core/features/sicherheit/crypto';
import { ladeTextHerunter } from 'core/utils/format';
import RecoveryDialog from 'core/features/sicherheit/RecoveryDialog';

/**
 * Erstmalige Einrichtung des verschluesselten Tresors: Passphrase waehlen,
 * optional eine Schluesseldatei als zweiten Faktor erzeugen. Nach der Einrichtung
 * ist der "gesicherte Modus" aktiv.
 */
export default function VaultSetup() {
  const dispatch = useDispatch();
  const [passphrase, setPassphrase] = useState('');
  const [wiederholung, setWiederholung] = useState('');
  const [mitKeyfile, setMitKeyfile] = useState(false);
  const [fehler, setFehler] = useState(null);
  const [laeuft, setLaeuft] = useState(false);
  const [recovery, setRecovery] = useState(null);

  const pruefe = () => {
    if (passphrase.length < 8) return 'Die Passphrase muss mindestens 8 Zeichen haben.';
    if (passphrase !== wiederholung) return 'Die Passphrasen stimmen nicht überein.';
    return null;
  };

  const absenden = async (e) => {
    e.preventDefault();
    const problem = pruefe();
    if (problem) {
      setFehler(problem);
      return;
    }
    setFehler(null);
    setLaeuft(true);
    try {
      const { recoveryCode, keyfileBase64 } = await dispatch(richteTresorEin({ passphrase, mitKeyfile })).unwrap();
      if (keyfileBase64) {
        ladeTextHerunter('tresor.key', baueKeyfileInhalt(keyfileBase64));
      }
      setRecovery({ recoveryCode, keyfileGeladen: Boolean(keyfileBase64) });
    } catch (ex) {
      setFehler('Einrichtung fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div className="mx-auto max-w-md">
      <div className="rounded-2xl border border-line bg-paper p-6 shadow-sm">
        <div className="mb-4 text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-brand-600/10 text-2xl">🔐</div>
          <h1 className="font-serif text-2xl font-semibold text-ink">Gesicherten Modus einrichten</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Deine gesicherten Bereiche werden lokal in deinem Browser verschlüsselt. Der Server sieht sie nie im Klartext.
          </p>
        </div>

        <form onSubmit={absenden} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Passphrase</label>
            <input
              type="password"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              autoComplete="new-password"
              className="w-full rounded-lg border border-line bg-paper px-3 py-2 text-ink focus:border-brand-600 focus:outline-none"
              placeholder="Mindestens 8 Zeichen"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Passphrase wiederholen</label>
            <input
              type="password"
              value={wiederholung}
              onChange={(e) => setWiederholung(e.target.value)}
              autoComplete="new-password"
              className="w-full rounded-lg border border-line bg-paper px-3 py-2 text-ink focus:border-brand-600 focus:outline-none"
            />
          </div>

          <label className="flex cursor-pointer items-start gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={mitKeyfile}
              onChange={(e) => setMitKeyfile(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-brand-600"
            />
            <span>
              Zusätzliche Schlüsseldatei als zweiten Faktor erzeugen
              <span className="block text-ink-muted">
                Wird als Datei heruntergeladen. Zum Entsperren brauchst du dann Passphrase <em>und</em> Datei.
              </span>
            </span>
          </label>

          {fehler && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{fehler}</p>}

          <button
            type="submit"
            disabled={laeuft}
            className="w-full rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500 disabled:opacity-50"
          >
            {laeuft ? 'Richte ein…' : 'Gesicherten Modus erstellen'}
          </button>
        </form>

        <p className="mt-4 text-center text-xs text-ink-muted">
          Achtung: Ohne Passphrase (bzw. Wiederherstellungs-Code) sind die Daten nicht wiederherstellbar.
        </p>
      </div>

      {recovery && (
        <RecoveryDialog
          recoveryCode={recovery.recoveryCode}
          keyfileGeladen={recovery.keyfileGeladen}
          onFertig={() => setRecovery(null)}
        />
      )}
    </div>
  );
}
